/**
 * Entrar al backoffice — correo, contraseña y **segundo factor**.
 *
 * La sesión de aquí NO es la del producto: la cookie es `__Host-...`, ligada al
 * host, y este subdominio es otro host. Entrar aquí no da acceso a la
 * aplicación del cliente ni al contrario, que es la mitad del sentido de
 * separar el subdominio (`OD-0020` D-01).
 *
 * `OD-0020` D-02 exige MFA para el operador. Quien no lo tenga activo pasará el
 * login y recibirá 403 `MFA_REQUIRED` en la primera vista; se le dice allí.
 */
import { useState } from "react";
import { api } from "../api/cliente";
import { t } from "../textos";

export function Acceso({ onEntrado }: { onEntrado: () => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [challenge, setChallenge] = useState<string | null>(null);
  const [codigo, setCodigo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  function mensajeDe(estado: number, esCodigo: boolean): string {
    if (estado === 429) return t.acceso.bloqueado;
    if (estado === 401) return esCodigo ? t.acceso.codigoInvalido : t.acceso.credenciales;
    return t.acceso.error;
  }

  async function entrar(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setEnviando(true);
    const r = await api.login(email.trim(), password);
    setEnviando(false);
    // La contraseña no se queda en memoria más de lo necesario.
    setPassword("");
    if (!r.ok) {
      setError(mensajeDe(r.estado, false));
      return;
    }
    if (r.datos.tipo === "AUTENTICADO") {
      onEntrado();
      return;
    }
    setChallenge(r.datos.challenge);
  }

  async function verificar(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (challenge === null) return;
    setError(null);
    setEnviando(true);
    const r = await api.loginMfa(challenge, codigo.trim());
    setEnviando(false);
    setCodigo("");
    if (!r.ok) {
      setError(mensajeDe(r.estado, true));
      return;
    }
    onEntrado();
  }

  return (
    <div className="acceso">
      <h1>{t.acceso.titulo}</h1>
      {error !== null ? (
        <p className="aviso" data-tono="error" role="alert">
          {error}
        </p>
      ) : null}

      {challenge === null ? (
        <form onSubmit={entrar} noValidate>
          <label htmlFor="email">
            <span>{t.acceso.email}</span>
            <input
              id="email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
            />
          </label>
          <label htmlFor="password">
            <span>{t.acceso.password}</span>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          <button type="submit" disabled={enviando || email.trim() === "" || password === ""}>
            {t.acceso.entrar}
          </button>
        </form>
      ) : (
        <form onSubmit={verificar} noValidate>
          <label htmlFor="codigo">
            <span>{t.acceso.codigo}</span>
            <input
              id="codigo"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              autoFocus
            />
          </label>
          <button type="submit" disabled={enviando || codigo.trim() === ""}>
            {t.acceso.verificar}
          </button>
        </form>
      )}
    </div>
  );
}
